export default function BSSContent() {
  const designElements = [
    { label: "Upholstery", detail: "Linen, cotton-linen blend, or boucle — natural fibres that breathe and age well" },
    { label: "Silhouette", detail: "Soft, rounded arms and low backs feel more relaxed than sharp, boxy frames" },
    { label: "Leg finish", detail: "Light oak or walnut legs add warmth without competing with the sofa colour" },
    { label: "Cushion fill", detail: "Feather-down or foam-fibre blend for a lived-in, comfortable look" },
    { label: "Tone", detail: "Warm beige, sand, or oat — avoid cool greys that read as clinical in natural light" },
    { label: "Scale", detail: "Choose depth and width based on how you actually sit — comfort matters as much as looks" },
  ];

  const stylingTips = [
    "Layer two or three cushion tones within the same warm neutral family — oat, sand, and warm ivory work beautifully together.",
    "Add a chunky knit or linen throw draped loosely over one arm to soften the silhouette.",
    "Place a light wood coffee table at a comfortable distance — roughly 40–45cm from the sofa edge.",
    "A textured neutral rug underneath grounds the sofa and defines the seating area without adding visual noise.",
    "Keep the wall behind the sofa simple — a single large artwork or a few small frames in warm tones works best.",
    "Avoid placing the sofa directly against the wall if space allows — even 10cm of breathing room makes the room feel more considered.",
    "Use warm-toned bulbs (2700K) in nearby lamps to bring out the warmth in the beige upholstery.",
  ];

  return (
    <article className="max-w-2xl mx-auto px-4 md:px-0 py-16">

      {/* Introduction */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Introduction
        </p>
        <p
          className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          A beige sofa is one of the most versatile and enduring choices you can make for a living room.
          It does not demand attention — it simply holds the room together.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          When chosen well, a beige sofa works across seasons, styles, and changing tastes.
          It pairs naturally with warm wood, soft linen, dried botanicals, and layered neutral textiles.
          The key is choosing the right tone, material, and scale for your specific space — and this guide walks you through exactly that.
        </p>
      </div>

      {/* Why Beige Works */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Why It Works
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          Why a Beige Sofa Works Better Than You Think
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/fb0022b989ab954f09ffd7359806c0ac.jpg"
            alt="Beige sofa texture and warmth"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          Beige is often dismissed as safe or boring — but that misses the point entirely.
          A warm beige sofa reads as calm, not bland. It reflects natural light softly,
          works with almost every other material in the room, and never fights for attention.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          The difference between a beige sofa that looks expensive and one that looks flat
          comes down to undertone. Warm beige — with hints of sand, oat, or cream — reads as
          rich and considered. Cool beige — with grey or green undertones — can feel clinical
          and lifeless, especially in rooms with limited natural light.
        </p>
        <p
          className="text-[#6B5E53] text-sm md:text-base leading-relaxed"
          style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
        >
          When you layer a warm beige sofa with natural textures — linen cushions, a wool rug,
          a light wood table — the room starts to feel genuinely warm and complete.
          It is one of the most forgiving and rewarding choices in interior design.
        </p>
      </div>

      {/* Key Design Elements */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          What to Look For
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          Key Design Elements to Consider
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="space-y-4">
          {designElements.map((el, i) => (
            <div key={i} className="flex gap-5 items-start border-b border-[#EDE6DA] pb-4 last:border-0">
              <span
                className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase w-24 flex-shrink-0 pt-0.5"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {el.label}
              </span>
              <p
                className="text-[#6B5E53] text-sm leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
              >
                {el.detail}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Styling Tips */}
      <div className="mb-14">
        <p
          className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          Styling
        </p>
        <h2
          className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
        >
          How to Style a Beige Sofa
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=beige%20sofa%20styled%20with%20linen%20cushions%20knit%20throw%20warm%20wood%20coffee%20table%20neutral%20rug%20dried%20flowers%20calm%20living%20room%20editorial%20interior%20photography%20warm%20light&width=900&height=500&seq=bss_style1&orientation=landscape"
            alt="Styled beige sofa with cushions and throw"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <ul className="space-y-4">
          {stylingTips.map((tip, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span
                className="text-[#C8BAA8] text-[10px] tracking-[0.3em] mt-1 flex-shrink-0"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                0{i + 1}
              </span>
              <p
                className="text-[#6B5E53] text-sm leading-relaxed"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
              >
                {tip}
              </p>
            </li>
          ))}
        </ul>
      </div>

    </article>
  );
}
